import { msalLogger } from './logger';

/**
 * Get the current origin (protocol + hostname + port) for dynamic redirect URI
 */
export const getCurrentOrigin = (): string => {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    msalLogger.debug('Dynamic redirect URI origin detected:', origin);
    return origin;
  }
  
  // Fallback to environment variable during SSR or initial load
  const fallback = process.env.REACT_APP_MSAL_REDIRECT_URI || 'http://localhost:3000';
  msalLogger.warn('Window not available, using fallback redirect URI:', fallback);
  return fallback;
};

/**
 * Get the full redirect URI for the current domain
 */
export const getDynamicRedirectUri = (): string => {
  const origin = getCurrentOrigin();
  
  // Use origin without trailing slash to match OIDC provider configuration
  const redirectUri = origin;
  
  msalLogger.debug('Dynamic redirect URI set to:', redirectUri);
  return redirectUri;
};

/**
 * Get the post-logout redirect URI for the current domain
 */
export const getDynamicPostLogoutRedirectUri = (): string => {
  // Use the same logic as redirect URI
  return getDynamicRedirectUri();
};

/**
 * Validate that the current origin is in the list of allowed domains
 * This is optional but recommended for security
 */
export const validateAllowedDomain = (allowedDomains?: string[]): boolean => {
  if (!allowedDomains || allowedDomains.length === 0) {
    return true; // No validation if no domains specified
  }
  
  const currentOrigin = getCurrentOrigin();
  const isAllowed = allowedDomains.some(domain => 
    currentOrigin.startsWith(domain) || currentOrigin === domain
  );
  
  if (!isAllowed) {
    msalLogger.error('Current domain not in allowed list:', {
      currentOrigin,
      allowedDomains
    });
  }
  
  return isAllowed;
};

/**
 * Handle critical authentication errors globally
 * This function can be called from anywhere in the app to handle auth errors
 */
export const handleCriticalAuthError = async (error: any): Promise<boolean> => {
  const { msalService } = await import('../services/msalService');
  
  if (msalService.shouldForceReauthentication(error)) {
    msalLogger.warn('Critical auth error detected globally, forcing reauthentication...');
    await msalService.forceReauthenticationPublic();
    return true;
  }
  
  return false;
};

/**
 * Check if an error is a critical authentication error
 */
export const isCriticalAuthError = (error: any): boolean => {
  if (!error || typeof error !== 'object') return false;
  
  const errorMessage = error.message || error.errorMessage || '';
  const errorCode = error.errorCode || error.error || '';
  
  const criticalErrors = [
    'AADSTS40008', // External identity provider error
    'AADSTS50166', // Request to External OIDC endpoint failed
    'AADSTS50058', // Silent sign-in request failed
    'AADSTS50079', // User needs to use multi-factor authentication
    'AADSTS50076', // User needs to use multi-factor authentication (different code)
    'AADSTS700082', // Expired refresh token
    'AADSTS700084', // Refresh token not found
    'AADSTS50020', // User account from identity provider does not exist
    'AADSTS65001', // The user or administrator has not consented
    'AADSTS65004', // User declined to consent to access the app
    'interaction_required',
    'login_required',
    'consent_required'
  ];
  
  return criticalErrors.some(criticalError => 
    errorMessage.includes(criticalError) || errorCode.includes(criticalError)
  );
};
